import {TimelineDivider} from '@github-ui/timeline-items/TimelineDivider'
import {VALUES} from '@github-ui/timeline-items/Values'
import {useAnalytics} from '@github-ui/use-analytics'
import {useLocalStorage} from '@github-ui/use-safe-storage/local-storage'
import {Button} from '@primer/react'
import {useCallback, useEffect, useId, useRef, useState} from 'react'

import {useScrollPreservation} from '../../hooks/use-safari-scroll-preservation'
import {LoadDirectionPicker} from './LoadDirectionPicker'
import styles from './LoadMore.module.css'

export type LoadMoreCallbackFn = (
  count: number,
  options?: {
    onComplete?: () => void
  },
) => void

/**
 * Whether activating the control loads everything remaining (so the control
 * unmounts) rather than a single page (so it persists). Shared with the
 * post-load focus logic so the two cannot drift apart.
 */
export const isLoadAllMode = (numberOfRemainingItems: number) =>
  numberOfRemainingItems < VALUES.timeline.maxPreloadCount

const getRemainingItemsBucket = (numberOfRemainingItems: number) => {
  if (numberOfRemainingItems < VALUES.timeline.pageSize) return 'under_one_page'
  if (numberOfRemainingItems < VALUES.timeline.pageSize * 2) return 'one_page'
  if (numberOfRemainingItems < VALUES.timeline.pageSize * 5) return 'two_to_four_pages'
  return 'five_or_more_pages'
}

export type LoadMoreProps = {
  numberOfRemainingItems: number
  loadFromTopFn: LoadMoreCallbackFn
  loadFromBottomFn: LoadMoreCallbackFn
  children: React.ReactNode
  type: 'load-top' | 'load-bottom'
  lastItemInTopTimelineIsComment: boolean
  firstItemInBottomTimelineIsComment: boolean
  isCurrentlyLoadingBackItems?: boolean
  newestFirst?: boolean
}

export const LoadMore = ({
  numberOfRemainingItems,
  loadFromTopFn,
  loadFromBottomFn,
  children,
  type,
  lastItemInTopTimelineIsComment,
  firstItemInBottomTimelineIsComment,
  isCurrentlyLoadingBackItems,
  newestFirst,
}: LoadMoreProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const {sendAnalyticsEvent} = useAnalytics({optional: true})
  const loadAllMode = isLoadAllMode(numberOfRemainingItems)
  const isLoadMoreDisabled = isLoading || Boolean(isCurrentlyLoadingBackItems)
  const [isHovering, setIsHovering] = useState<'top' | 'bottom' | undefined>(undefined)
  const [loadMoreFromTopPreference, setLoadMoreFromTopPreference] = useLocalStorage(
    'loadMoreFromTopPreference',
    loadFromTopFn !== undefined,
  )
  const showBottomDivider = numberOfRemainingItems === 0 || firstItemInBottomTimelineIsComment
  const showTopDivider = lastItemInTopTimelineIsComment
  const descriptionId = useId()
  const lastShownEventKeyRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (isLoadMoreDisabled) return

    const eventKey = `${type}:${numberOfRemainingItems}`
    if (lastShownEventKeyRef.current === eventKey) return
    lastShownEventKeyRef.current = eventKey

    sendAnalyticsEvent('issue_viewer.timeline_load_more.shown', 'ISSUE_TIMELINE_LOAD_MORE', {
      position: type,
      mode: loadAllMode ? 'load_all' : 'load_more',
      remaining_items_bucket: getRemainingItemsBucket(numberOfRemainingItems),
    })
  }, [isLoadMoreDisabled, loadAllMode, numberOfRemainingItems, sendAnalyticsEvent, type])

  // Safari doesn't support CSS scroll anchoring, so we need to manually preserve scroll position
  const {captureScrollPosition, restoreScrollPosition} = useScrollPreservation()

  const loadMore = useCallback(
    (loadAll = false, shouldLoadFromTop = true, activation: 'primary' | 'direction_menu' = 'primary') => {
      if (isLoadMoreDisabled) return

      sendAnalyticsEvent('issue_viewer.timeline_load_more.clicked', 'ISSUE_TIMELINE_LOAD_MORE', {
        position: type,
        mode: loadAll ? 'load_all' : 'load_more',
        direction: (newestFirst ? !shouldLoadFromTop : shouldLoadFromTop) ? 'older' : 'newer',
        activation,
        remaining_items_bucket: getRemainingItemsBucket(numberOfRemainingItems),
      })

      // Capture scroll position before loading more items (Safari only)
      captureScrollPosition()

      const loadCount = loadAll ? numberOfRemainingItems : VALUES.timeline.pageSize
      setIsLoading(true)
      setIsHovering(undefined)
      setLoadMoreFromTopPreference(shouldLoadFromTop)

      const onComplete = () => {
        // Restore scroll position after items are loaded (Safari only)
        restoreScrollPosition()
        setIsLoading(false)
      }

      if (shouldLoadFromTop) {
        if (loadFromTopFn) {
          loadFromTopFn(loadCount, {
            onComplete,
          })
        }
      } else {
        if (loadFromBottomFn) {
          loadFromBottomFn(loadCount, {
            onComplete,
          })
        }
      }
    },
    [
      captureScrollPosition,
      isLoadMoreDisabled,
      loadFromBottomFn,
      loadFromTopFn,
      newestFirst,
      numberOfRemainingItems,
      restoreScrollPosition,
      sendAnalyticsEvent,
      setLoadMoreFromTopPreference,
      type,
    ],
  )

  const renderButton = useCallback(() => {
    return (
      <Button
        onClick={() => {
          loadMore(loadAllMode, loadAllMode || loadMoreFromTopPreference)
        }}
        inactive={isLoadMoreDisabled}
        aria-disabled={isLoadMoreDisabled}
        data-testid={`issue-timeline-load-more-${type}`}
        aria-describedby={descriptionId}
      >
        <div className={styles.buttonChildrenWrapper}>{children}</div>
      </Button>
    )
  }, [children, descriptionId, isLoadMoreDisabled, loadAllMode, loadMore, loadMoreFromTopPreference, type])

  return (
    <div data-testid={`issue-timeline-load-more-wrapper-${type}`} className={styles.LoadMoreContainer}>
      {showTopDivider && (
        <TimelineDivider isLoading={isLoading && loadMoreFromTopPreference} isHovered={isHovering === 'top'} />
      )}
      <div data-testid={`issue-timeline-load-more-container-${type}`} className={styles.loadMoreContentContainer}>
        <div className={styles.innerContentWrapper}>
          <div className={styles.centeredContent}>
            <div className={styles.headingWrapper}>
              <h3 id={descriptionId} className={styles.remainingItemsHeading}>
                <span data-testid={`issue-timeline-load-more-count-${type}`}>{numberOfRemainingItems}</span>
                <span> remaining {numberOfRemainingItems === 1 ? <span>item</span> : <span>items</span>}</span>
              </h3>
            </div>
            <div className={styles.buttonWrapper}>{renderButton()}</div>
          </div>
          {!loadAllMode && (
            <LoadDirectionPicker
              loadFromTopFn={() => {
                loadMore(false, true, 'direction_menu')
              }}
              loadFromBottomFn={() => {
                loadMore(false, false, 'direction_menu')
              }}
              isLoading={isLoadMoreDisabled}
              setIsHovering={setIsHovering}
              type={type}
              newestFirst={newestFirst}
            />
          )}
        </div>
      </div>
      {showBottomDivider && (
        <TimelineDivider
          isLoading={(isLoading && !loadMoreFromTopPreference) || isCurrentlyLoadingBackItems}
          isHovered={isHovering === 'bottom'}
        />
      )}
    </div>
  )
}
